import React, {useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import constants from 'helpers/constants';
import DialogContent from 'components/atoms/Dialogs/DialogContent/DialogContent';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import DialogFooter from 'components/molecules/Dialogs/DialogFooter/DialogFooter';
import Button from 'components/atoms/Buttons/Button/Button';
import DialogHeader from 'components/molecules/Dialogs/DialogHeader/DialogHeader';
import {Span} from 'components/atoms/Text/Typography/Typography';
import utils from 'helpers/utils';
import Icon from 'components/atoms/Icons/Icon/Icon';
import Save from '@mui/icons-material/Save';
import Close from '@mui/icons-material/Close';
import Checklist from '@mui/icons-material/Checklist';
import StyledHostnamesDialog from 'components/organisms/Dialogs/HostnamesDialog/HostnamesDialog.styles';

const HostnamesDialog = React.forwardRef((props, ref) => {
  const {
    hostnames,
    onChange,
    onSubmit,
    onClose,
    ...innerProps
  } = useComponentProps(props, 'HostnamesDialog');

  const innerRef = useRef(null);
  const formRef = useRef(null);
  const fieldRef = useRef(null);

  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(null);
  const [validation, setValidation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [clearable, setClearable] = useState(false);

  useImperativeHandle(ref, () => innerRef.current);

  const options = useMemo(() => {
    return hostnames.map((h) => ({hostname: h, label: h.hostname, value: h.hostname, disabled: !h.canExclude}));
  }, [hostnames]);

  useEffect(() => {
    setClearable(hostnames.filter((hostname) => !hostname.excluded && hostname.canExclude).length > 0);
  }, [hostnames]);

  const fields = useMemo(() => {
    return [{
      name: 'hostnames',
      type: constants.formFieldTypes.list,
      validation: constants.formFieldValidationTypes.list,
      conversion: constants.formFieldConversionTypes.value,
      initial: options.filter((option) => !option.hostname.excluded),
      FormFieldProps: {
        ref: fieldRef,
        hiddenLabel: true,
        multiple: true,
        ListProps: {
          catchFocus: false
        }
      },
      options
    }]
  }, [options]);

  const handleChange = (field, value) => {
    onChange?.(field, value);
    setClearable(value.filter((v) => !options.find((opt) => opt.value === v).disabled).length > 0);
  };

  const handleSubmit = (values, actions) => {
    setSubmitting(true);

    utils.asPromise(onSubmit)(values['hostnames'])
      .then(() => {
        onClose?.(null, 'saveButtonClick');
      })
      .catch(() => {
        setError('Saving hostnames failed');
      })
      .finally(() => {
        actions.setSubmitting(false);
        setSubmitting(false);
      });
  };

  const handleValidating = (isValidating, isDirty, hasErrors) => {
    setDirty(isDirty);
    if (hasErrors) {
      setValidation('Please check if all fields have the correct values');
    } else {
      setValidation(null);
    }
  }

  const handleSubmitClick = () => {
    formRef.current?.submit();
  };

  const handleCancel = (e) => {
    innerRef.current?.close?.(e);
  }

  const handleClose = (e, reason) => {
    if ((!submitting && !error && !dirty) || ['escapeKeyDown', 'closeButtonClick', 'cancelButtonClick'].includes(reason)) {
      onClose?.(e, reason);
    }
  }

  const handleClearClick = () => {
    fieldRef.current?.setValue?.(options.filter((option) => option.disabled));
  }

  const renderButtons = () => {
    return <React.Fragment>
      <Button variant="text"
              children="Clear"
              startIcon={<Icon icon={Checklist}/>}
              disabled={!clearable || submitting}
              onClick={handleClearClick}/>
      <Button children={'Cancel'}
              variant="text"
              startIcon={<Icon icon={Close}/>}
              onClick={handleCancel}/>
      <Button disabled={submitting}
              type="submit"
              variant="contained"
              children={'Confirm'}
              startIcon={<Icon icon={Save}/>}
              onClick={handleSubmitClick}/>
    </React.Fragment>
  }

  return <StyledHostnamesDialog ref={innerRef} {...innerProps} onClose={handleClose}>
    <DialogHeader title="Hostnames" />
    <DialogContent>
      <InlineForm ref={formRef} fields={fields}
                  onValidating={handleValidating}
                  onChange={handleChange}
                  onSubmit={handleSubmit} />
    </DialogContent>
    <DialogFooter className="HostnamesDialog-footer"
                  variant="split"
                  info={(error ?? validation) ? <Span color="error">{error ?? validation}</Span> : null}
                  buttons={renderButtons()} />
  </StyledHostnamesDialog>
});

HostnamesDialog.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  hostnames: PropTypes.array,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func
};

export default HostnamesDialog;
